/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { IframSettings } from './irame.Settings';

const rgbToHex = rgb => {
  if (typeof rgb !== 'string') {
    return null;
  }
  const [r, g, b] = rgb.trim().split(/\s+/).map(Number);
  if (![r, g, b].every(value => Number.isFinite(value))) {
    return null;
  }
  return (
    '#' +
    [r, g, b]
      .map(value =>
        Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0')
      )
      .join('')
  );
};

const THEME_MESSAGE_TYPES = new Set([
  'estynoss-theme-ready',
  'estynoss-theme-request',
  'theme-ready',
  'theme-request',
]);

const getUrlOrigin = url => {
  try {
    return new URL(url).origin;
  } catch (e) {
    return '*';
  }
};

export const IframeAll = props => {
  const { itemId, iframeId, iframe, canEdit } = props;
  const iframeRef = useRef(null);
  const theme = useSelector(state => state.global.theme);

  const iframeTheme = useMemo(
    () => ({
      main: rgbToHex(theme?.main),
      mainD: rgbToHex(theme?.['main-d']),
      contrast: rgbToHex(theme?.contrast),
      contrastL: rgbToHex(theme?.['contrast-l'] ?? theme?.contrast),
      primary: rgbToHex(theme?.primary),
      primaryL: rgbToHex(theme?.['primary-l']),
      primaryD: rgbToHex(theme?.['primary-d']),
      secondary: rgbToHex(theme?.secondary),
      secondaryL: rgbToHex(theme?.['secondary-l'] ?? theme?.secondary),
      secondaryD: rgbToHex(theme?.['secondary-d']),
      red: rgbToHex(theme?.red),
      orange: rgbToHex(theme?.orange),
      yellow: rgbToHex(theme?.yellow),
      green: rgbToHex(theme?.green),
      lime: rgbToHex(theme?.lime),
      purple: rgbToHex(theme?.purple),
      blue: rgbToHex(theme?.blue),
      pink: rgbToHex(theme?.pink),
    }),
    [theme],
  );

  const iframeOrigin = useMemo(() => getUrlOrigin(iframe?.url), [iframe?.url]);

  const postThemeToIframe = useCallback((targetOrigin = iframeOrigin) => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (!contentWindow) {
      return;
    }
    contentWindow.postMessage(
      {
        type: 'theme',
        theme: iframeTheme,
      },
      targetOrigin || '*',
    );
  }, [iframeOrigin, iframeTheme]);

  const postThemeToIframeWithRetries = useCallback(() => {
    postThemeToIframe();
    const delays = [100, 300, 700, 1500];
    const timeoutIds = delays.map(delay =>
      window.setTimeout(() => postThemeToIframe(), delay)
    );
    return () => timeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
  }, [postThemeToIframe]);

  useEffect(() => {
    return postThemeToIframeWithRetries();
  }, [postThemeToIframeWithRetries]);

  useEffect(() => {
    const handleThemeRequest = event => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      if (!THEME_MESSAGE_TYPES.has(event.data?.type)) {
        return;
      }
      postThemeToIframe(event.origin === 'null' ? '*' : event.origin);
    };

    window.addEventListener('message', handleThemeRequest);
    return () => window.removeEventListener('message', handleThemeRequest);
  }, [postThemeToIframe]);

  if (!iframe) return null;
  return (
    <div className='h-full'>
      <IframSettings
        itemId={itemId}
        iframeId={iframeId}
        iframe={iframe}
        canEdit={canEdit}
      />
      {iframe.url?.length < 1 ? (
        <div>configure url</div>
      ) : (
        <iframe
          ref={iframeRef}
          src={iframe.url}
          width='100%'
          height='100%'
          frameBorder='0'
          allowFullScreen
          title='iframe'
          onLoad={postThemeToIframeWithRetries}
        />
      )}
    </div>
  );
};
